/**
 * Who can see a report RUN — the queue drawer's "Visibility" line (spec 167).
 *
 * ## What the line used to say, and why it was false
 *
 * `JobRunSummary` printed **"Only me"** on every run the reader owned. Seen live on QA
 * (vladdemo, 2026-10-12): run 4058d0f0's drawer read "VISIBILITY Only me" while the
 * editor of the schedule that produced it, 38bb1d8f, read "Who can see this: Everyone
 * at the firm". Measured against the running system, the EDITOR is right about the
 * schedule and the DRAWER is wrong about the run:
 *
 *   - D1 `schedules.scope` for 38bb1d8f is `'firm'`, so the editor's sentence is the
 *     stored fact — about the SCHEDULE, which colleagues see on their Schedules page.
 *   - `jobs` has no scope column at all. A firing copies nothing about audience from
 *     its schedule (`runner.js` `buildScheduleJob`), and the Queue's "Everyone at the
 *     firm" list is `WHERE tenant=? AND mode=?` (`store.js` `listJobs`) — every run at
 *     the firm, whoever ran it and whatever its schedule says. ("My group" is a subset:
 *     the members of the reader's real groups, CB's built-in Everyone / Administrators
 *     excluded since CC-53053. The firm list alone is what makes the line true.)
 *   - So the run is LISTED to everyone at the firm: report, status, when, row count,
 *     duration, trigger. Measured: CBS's firm-scope list carried three runs of another
 *     user's schedule d1de28c2, whose own scope is `'mine'` — a private schedule's runs
 *     are listed firm-wide all the same.
 *   - The RESULT is not shared. `getJob` is owner-scoped in SQL, so `/jobs/:id` and
 *     `/jobs/:id/result` 404 for anyone else (CC-50231, verified live), and a scheduled
 *     run's result key is per-user (`shareable: false` on every firing).
 *
 * "Only me" was therefore a claim that a run is private when its listing is not — the
 * dangerous direction for a firm reading a compensation or A/R report's run. The line
 * now states both halves. (Spec 167 stated them identically for EVERY run, because then
 * neither half depended on the run's schedule or on who was reading. ⚠ Superseded by
 * spec 169, below: both halves now depend on the run's schedule, and the second on the
 * reader too — the facts above about d1de28c2 and `listJobs` describe the system BEFORE
 * spec 169.)
 *
 * ## What this deliberately does not do
 *
 * It does not read the schedule's scope. A schedule's "Who can see this" governs the
 * schedule, not its runs, and quoting it here would put the old contradiction back in a
 * new place. Whether a run SHOULD be listed firm-wide is an open product question
 * (`cb-api/src/domains/reports/scope.js`), not something a label can settle.
 *
 * ## Spec 169 (CC-54214) — the question was settled, for PRIVATE schedules
 *
 * Jordan, 2026-10-13: "only admins should see and be able to edit schedules set to mine in
 * the firm wide queue", and admins may open and download those runs' results. cb-api now
 * decides who a run is LISTED to from its schedule, server-side, and says so on every
 * row and detail (`privateListing`, `privateSchedule`, `adminAccess`):
 *
 *   - a run of a 'mine' schedule — or of a schedule that was deleted, whose scope can no
 *     longer be read (cb-api fails closed) — is listed only to its OWNER and to Centerbase
 *     administrators;
 *   - its result opens for its owner and, when the schedule is provably private, for an
 *     administrator (`adminAccess`) — never for anyone else;
 *   - an ad-hoc run, and a run of a group or firm schedule, is listed for everyone at the
 *     firm exactly as before, and only the person who ran it can open its result.
 *
 * The line still does not read the schedule's scope itself: it reads the server's answer
 * about the RUN. An older worker sends none of the three flags; it also still lists every
 * run firm-wide, so the old sentence is the true one for it, and that is what absent means.
 */

/** The run's listing for an ad-hoc run or a shared schedule's run, owned or not. */
export const RUN_LISTED_FIRM_WIDE = 'Listed for everyone at the firm'

/** Spec 169: the listing of a private (or unreadable) schedule's run. */
export const RUN_LISTED_PRIVATE = Object.freeze({
  owner: 'Listed for you and Centerbase administrators',
  other: 'Listed for its owner and Centerbase administrators',
})

/** Who can open the stored result: only the person who ran it. */
export const RESULT_OWNER_ONLY = Object.freeze({
  owner: 'only you can open its result',
  teammate: 'only the person who ran it can open its result',
})

/** Spec 169: who can open a PRIVATE schedule's run — its owner and administrators. */
export const RESULT_OWNER_AND_ADMINS = Object.freeze({
  owner: 'only you and administrators can open its result',
  admin: 'its owner and administrators can open its result',
})

/**
 * @param {object} job a normalized job; `isOwner` is absent on a mine-scope row, and
 *   `!== false` keeps that meaning "mine", the rule every queue surface uses. Spec 169:
 *   `privateListing` / `privateSchedule` / `adminAccess` are the server's answer; each is
 *   true only when it says exactly `true`.
 * @returns {{ value: string, note: string }}
 */
export const runVisibility = (job) => {
  const owned = job?.isOwner !== false
  if (job?.privateListing !== true) {
    return { value: RUN_LISTED_FIRM_WIDE, note: owned ? RESULT_OWNER_ONLY.owner : RESULT_OWNER_ONLY.teammate }
  }
  if (owned) {
    return {
      value: RUN_LISTED_PRIVATE.owner,
      // A deleted schedule's run is listed privately but is NOT opened for administrators
      // (its schedule cannot be shown to have been private), so it stays "only you".
      note: job.privateSchedule === true ? RESULT_OWNER_AND_ADMINS.owner : RESULT_OWNER_ONLY.owner,
    }
  }
  return {
    value: RUN_LISTED_PRIVATE.other,
    note: job.adminAccess === true ? RESULT_OWNER_AND_ADMINS.admin : RESULT_OWNER_ONLY.teammate,
  }
}

/*
 * Spec 169 — the schedule editors' words for a PRIVATE ('mine') schedule, shared by the
 * create modal (`QueueReportModal`) and the edit modal (`ReportSchedules`), so one
 * control never says two things. "Only me" stopped being true when Jordan gave
 * Centerbase administrators the right to see and edit private schedules (2026-10-13).
 */
export const PRIVATE_SCOPE_OPTION_LABEL = 'Only me and administrators'
export const PRIVATE_SCHEDULE_HELP =
  'Only you and Centerbase administrators can see this schedule. Administrators can also edit or pause it and open its results. It always runs as you, with your access to data, and its email goes where you set it.'

/**
 * Spec 169 — may the reader OPEN this run's stored result (Open report, View report,
 * Download)? Their own run, or — when cb-api said so for this reader — another user's
 * private-schedule run that they may open as a CB administrator. Strictly `=== true`:
 * an absent or garbled flag never opens anything. Dismiss and Cancel stay owner-only;
 * this is not ownership.
 */
export const canOpenRunResult = (job) => job?.isOwner !== false || job?.adminAccess === true
